import axios from 'axios'
import {put, take, takeLatest, call, select, race, delay} from "redux-saga/effects"
import {userSlice} from './userSlice'
import {network} from '../../utils/network'
import {appSlice} from '../app/appSlice'
import {endpointSlice} from '../endpoint/endpoingSlice'
import * as Sentry from '@sentry/react'
import {notificationsSlice} from "../notifications/notificationsSlice"
import {employeesSlice} from "../employees/employeesSlice"

function* userBalances() {
  try {
    const balancesRes = yield network.axios({
      method: 'GET',
      url: '/user-balance/external_balances/',
    })

    yield put(userSlice.actions.setUserBalances({
      balances: balancesRes?.data,
    }))
  } catch (e) {
    console.log(e)
  }
}

function* refreshUserBalances() {
  yield put(userSlice.actions.setBalancesRefreshing(true))
  try {
    const balancesRes = yield network.axios({
      method: 'POST',
      url: '/user-balance/refresh_external_balances/',
    })

    yield put(userSlice.actions.setUserBalances({
      balances: balancesRes?.data,
    }))
  } catch (e) {
    console.log(e)
  } finally {
    yield put(userSlice.actions.setBalancesRefreshing(false))
  }
}

function* userLimit() {
  const user = yield select(store => store.user)

  try {
    const limitsRes = yield network.axios({
      method: 'GET',
      url: '/user-limit/',
      params: {
        user_id: user?.permissions?.user_id,
      }
    })
    const limits = typeof limitsRes.data === 'object' && limitsRes.data.length > 0 ? limitsRes.data[0] : null

    yield put(userSlice.actions.setUserLimit({
      limits,
    }))
  } catch (e) {
    console.log(e)
  }
}

/**
 * Справочники зависят от мерчанта: бэк принимает необязательный merchant_id и без
 * него отдаёт объединение по всем мерчантам пользователя. Для юзера платформы это
 * была бы смесь всех заказчиков, поэтому передаём мерчанта выбранной точки.
 */
function* fetchDirectories() {
  const selectedEndpoint = yield select(store => store.endpoint.selectedEndpoint)
  const merchantId = selectedEndpoint?.merchant_id ?? null
  const loadedForMerchantId = yield select(store => store.app.directoriesMerchantId)

  if (merchantId && merchantId === loadedForMerchantId) {
    // Отметку ставим и здесь: fetchUser ждёт её как сигнал «справочники готовы».
    yield put(appSlice.actions.setDirectoriesMerchantId(merchantId))
    return
  }

  try {
    const response = yield network.axios({
      method: 'GET',
      url: '/directories/',
      ...(merchantId ? {params: {merchant_id: merchantId}} : {}),
    })
    yield put(appSlice.actions.setDirectories(response.data))
    yield put(appSlice.actions.setDirectoriesMerchantId(merchantId))
  } catch (e) {
    console.error(e)
    // Сбрасываем отметку: справочников нет, при следующем выборе точки надо
    // попробовать снова. И это же снимает ожидание в fetchUser.
    yield put(appSlice.actions.setDirectoriesMerchantId(null))
  }
}

/**
 * Раскладка ответа /user-merchant/ в permissions: по записи на каждую точку,
 * чтобы страницы читали права как permissions.endpoints[endpointId].
 */
function buildPermissions(permissionsData) {
  const permissions = {
    user_id: permissionsData?.[0]?.user_id,
    user_data: permissionsData?.[0]?.user_data,
    endpoints: {},
  }

  permissionsData?.forEach(permission => {
    permission?.endpoint_permissions?.forEach(ep => {
      const {
        merchant_settings,
        merchant_passport_check_counter,
        merchant_passport_check_limit,
        merchant_personal_account_settings,
        merchant_id,
        merchant_car_check_counter,
        merchant_car_check_limit,
        merchant_permissions,
        merchant_is_demo,
      } = permission
      permissions.endpoints[ep.endpoint_id] = {
        merchant_settings,
        merchant_passport_check_counter,
        merchant_passport_check_limit,
        merchant_car_check_counter,
        merchant_car_check_limit,
        personal_account_settings: merchant_personal_account_settings?.[0] || null,
        merchant_id,
        merchant_is_demo,
        merchant_permissions: merchant_permissions?.[0] || null
      }
      const {user_id, endpoint_id, ...rest} = ep
      Object.entries(rest).forEach(([key, value]) => {
        permissions.endpoints[ep.endpoint_id][key] = Boolean(value)
      })
    })
  })

  return permissions
}

/**
 * Перечитывает права: нужен после создания компании — её точка появляется в
 * /endpoint/, но без записи в permissions.endpoints страницы считают, что прав
 * на ней нет, и рисуют урезанный интерфейс.
 */
function* refreshPermissions(action) {
  if (action?.type === userSlice.actions.logout.type) return
  try {
    const permissionsRes = yield network.axios({
      method: 'GET',
      url: '/user-merchant/',
    })
    const permissionsData = permissionsRes?.data
    if (!permissionsData?.length) {
      return
    }

    const permissions = buildPermissions(permissionsData)

    localStorage.setItem('permissions', JSON.stringify(permissions))
    localStorage.setItem('allPermissions', JSON.stringify(permissionsData))

    yield put(userSlice.actions.setPermissions({
      permissions,
      allPermissions: permissionsData,
    }))
  } catch (e) {
    console.error(e)
  }
}

function* fetchUser(payload) {
  yield put(userSlice.actions.setLoading(true))
  yield put(userSlice.actions.setTokenLoading(true))
  yield put(userSlice.actions.setGetMy(null))

  const {access, refresh} = payload

  try {
    network.axiosUpdate(access, refresh)
    yield put(userSlice.actions.setUser({
      user: {},
    }))
    yield put(userSlice.actions.setTokenLoading(false))

    // Балансы приходят по точкам, поэтому их грузит userBalances по
    // selectEndpoint — здесь запрос был бы вторым за тот же логин.
    const permissionsRes = yield network.axios({
      method: 'GET',
      url: '/user-merchant/',
    })


    yield put(userSlice.actions.fetchGetMy())

    const permissionsData = permissionsRes?.data

    const permissions = buildPermissions(permissionsData)

    const limitsRes = yield network.axios({
      method: 'GET',
      url: '/user-limit/',
      params: {
        user_id: permissions?.user_id,
      }
    })

    const limits = typeof limitsRes.data === 'object' && limitsRes.data.length > 0 ? limitsRes.data[0] : null

    Sentry.setUser({
      id: permissions.user_id,
      username: permissions.user_data.name
    });

    const posthog = window.posthog
    if (posthog) {
      if (typeof posthog.capture === 'function') {
        posthog.capture('user_signed_up', {
          user_id: permissions.user_id,
          user_name: permissions.user_data.name,
          merchant_id: permissions.merchant_id
        })
      }

      if (typeof posthog.setPersonProperties === 'function') {
        posthog.setPersonProperties(
          {name: permissions.user_data.name}
        )
      }
    }

    const tokensToSave = network.getLastAuthTokens() ?? {access, refresh}

    localStorage.setItem('tokens', JSON.stringify(tokensToSave))
    localStorage.setItem('permissions', JSON.stringify(permissions))
    localStorage.setItem('allPermissions', JSON.stringify(permissionsData))


    yield put(userSlice.actions.setUser({
      user: {},
      tokens: tokensToSave,
      permissions,
      balances: null,
      limits,
      allPermissions: permissionsData
    }))

    yield put(endpointSlice.actions.fetch())
    yield take(endpointSlice.actions.selectEndpoint.type)

    // Справочники грузит fetchDirectories по selectEndpoint. Ждём их здесь,
    // чтобы логин завершался с готовым стором, но не дольше таймаута: упавший
    // запрос не должен оставить пользователя под лоадером.
    const selectedMerchantId = yield select(store => store.endpoint.selectedEndpoint?.merchant_id)
    const directoriesMerchantId = yield select(store => store.app.directoriesMerchantId)

    // Если справочники уже загружены для этой точки, fetchDirectories отвечает
    // синхронно — сигнал успел бы уйти до подписки, и ожидание висело бы до
    // таймаута.
    if (!selectedMerchantId || selectedMerchantId !== directoriesMerchantId) {
      yield race({
        loaded: take(appSlice.actions.setDirectoriesMerchantId.type),
        timeout: delay(10000),
      })
    }
  } catch (e) {
    console.error(e)

    if (e?.code === 'ERR_NETWORK') {
      yield put(appSlice.actions.addMessage({
        type: 'error',
        text: 'Ошибка доступа к серверу',
      }))
    } else {
      if (e?.response?.status === 401) {
        if (e?.response?.data?.code === "token_not_valid") {
          yield put(appSlice.actions.addMessage({
            type: 'error',
            text: 'Сессия закончилась. Авторизуйтесь заново',
          }))
        } else {
          yield put(appSlice.actions.addMessage({
            type: 'error',
            text: e?.response?.data?.detail ?? 'Что-то пошло не так',
          }))
        }
      } else {
        yield put(appSlice.actions.addMessage({
          type: 'error',
          text: 'Что-то пошло не так',
        }))
      }
    }
  }
  yield put(userSlice.actions.setLoading(false))
  yield put(userSlice.actions.setTokenLoading(false))
}

function* fetchGetMy(action) {
  // Logout cancels an in-flight profile request before it can restore old roles.
  if (action?.type === userSlice.actions.logout.type) return
  try {
    let responseGetMy

    const hasActiveEndpoint = !!localStorage.getItem('endpoint')

    // if (!hasActiveEndpoint) {
    responseGetMy = yield network.axios({
      method: 'GET',
      url: '/employee/get_my/',
    })
    // }

    yield put(userSlice.actions.setGetMy(responseGetMy?.data))

    const activeEndpointID = responseGetMy?.data?.endpoint?.id

    if (activeEndpointID) {
      localStorage.setItem('endpoint', activeEndpointID)
    }

  } catch (e) {
    console.log(e);
  }
}


function* restore() {
  yield put(userSlice.actions.setLoading(true))
  yield put(userSlice.actions.setTokenLoading(true))


  try {
    const tokens = JSON.parse(localStorage.getItem('tokens'))
    const permissions = JSON.parse(localStorage.getItem('permissions'))
    if (!tokens || !permissions) {
      yield put(userSlice.actions.logout())
      yield put(userSlice.actions.setLoading(false))
      yield put(userSlice.actions.setTokenLoading(false))
      return
    }

    const {access, refresh} = tokens
    if (!access || !refresh) {
      yield put(userSlice.actions.logout())
      yield put(userSlice.actions.setLoading(false))
      yield put(userSlice.actions.setTokenLoading(false))
      return
    }

    yield call(fetchUser, {access, refresh})

  } catch (e) {
    console.error(e)
  }

  yield put(userSlice.actions.setLoading(false))
  yield put(userSlice.actions.setTokenLoading(false))
}

function* login({payload}) {
  yield put(userSlice.actions.setTokenLoading(true))
  yield put(userSlice.actions.setLoading(true))

  const {username, password, offer_accepted} = payload
  try {
    const tokenRes = yield network.axios({
      method: 'POST',
      url: '/token/',
      data: {
        username,
        password,
        offer_accepted
      }
    })
    const {access, refresh, otp_enabled} = tokenRes.data

    yield put(userSlice.actions.setShowTokenOfferAccepted(false))

    if (otp_enabled && !access && !refresh) {
      yield put(userSlice.actions.setOtp(tokenRes.data))
    } else {
      yield call(fetchUser, {access, refresh})
    }

    // const user = yield select(store => store.user)
    // const employeeEndpointId = user?.permissions?.user_data?.employee_endpoint_id
    // const endpoints = user?.permissions?.endpoints

    // const employeeId = user?.permissions?.user_data?.employee_id
    // const localStorageEndpointId = localStorage.getItem('endpoint')

    // const endpointId = localStorageEndpointId || employeeEndpointId || Object.keys(endpoints)?.[0];
    // if (endpointId && employeeId) {
    //   yield put(employeesSlice.actions.updateEmployeeEndpoint({
    //     id: employeeId,
    //     endpoint_id: endpointId
    //   }))
    // }

  } catch (e) {
    console.error(e)

    if (e?.code === 'ERR_NETWORK') {
      yield put(appSlice.actions.addMessage({
        type: 'error',
        text: 'Ошибка доступа к серверу',
      }))
    } else {
      if (e?.response?.data?.detail === 'Пользователь не принял оферту') {
        yield put(userSlice.actions.setShowTokenOfferAccepted(true))
      }
      if (e?.response?.status === 401) {
        yield put(appSlice.actions.addMessage({
          type: 'error',
          text: e?.response?.data?.detail ?? 'Что-то пошло не так',
        }))
      } else if (e?.response?.data?.detail === 'User must only use API') {
        yield put(appSlice.actions.addMessage({
          type: 'error',
          text: 'Пользователь имеет доступ только к работе через API',
        }))
      } else {

        yield put(appSlice.actions.addMessage({
          type: 'error',
          text: 'Что-то пошло не так',
        }))
      }

    }
    yield put(userSlice.actions.logout())
  }

  yield put(userSlice.actions.setLoading(false))
  yield put(userSlice.actions.setTokenLoading(false))
}


function* otpCheck({payload}) {
  yield put(userSlice.actions.setLoading(true))
  yield put(userSlice.actions.setTokenLoading(true))
  const {code, username} = payload

  try {
    const tokenRes = yield network.axios({
      method: 'POST',
      url: '/otp/check/',
      data: {
        code,
        username,
      },
      headers: {
        ['Authorization']: null,
      }
    })
    const {access, refresh} = tokenRes.data
    yield call(fetchUser, {access, refresh})

  } catch (e) {
    if (e?.response?.data?.message === 'Code has expired') {
      yield put(userSlice.actions.logout())
      yield put(appSlice.actions.addMessage({
        type: 'error',
        text: e?.response?.data?.detail ?? 'Что-то пошло не так',
      }))
      yield put(userSlice.actions.setLoading(false))
      yield put(userSlice.actions.setTokenLoading(false))
      return
    }
    if (e?.response?.data?.message === 'User disabled') {
      yield put(appSlice.actions.addMessage({
        type: 'error',
        text: e?.response?.data?.detail ?? 'Что-то пошло не так',
      }))
      yield put(userSlice.actions.setUserDisabled(true))
      yield put(userSlice.actions.setLoading(false))
      yield put(userSlice.actions.setTokenLoading(false))

      return
    }
    if (e?.code === 'ERR_NETWORK') {
      yield put(appSlice.actions.addMessage({
        type: 'error',
        text: 'Ошибка доступа к серверу',
      }))
    } else {
      if (e?.response?.status === 401) {
        yield put(appSlice.actions.addMessage({
          type: 'error',
          text: e?.response?.data?.detail ?? 'Что-то пошло не так',
        }))
      } else {
        yield put(appSlice.actions.addMessage({
          type: 'error',
          text: 'Что-то пошло не так',
        }))
      }
    }
  }

  yield put(userSlice.actions.setLoading(false))
  yield put(userSlice.actions.setTokenLoading(false))
}


function* alfaLogin({payload}) {
  yield put(userSlice.actions.setLoading(true))
  yield put(userSlice.actions.setTokenLoading(true))
  const {code, state, redirect_url, client_id} = payload || {}
  console.log('step 3');

  const user = yield select(store => store.user)
  const tokens = user?.tokens

  if (tokens?.access || tokens?.refresh) {
    yield put(userSlice.actions.setTokenLoading(false))
    yield put(userSlice.actions.setLoading(false))
    return
  }
  console.log('step 5');

  try {
    const tokenRes = yield network.axios({
      method: 'POST',
      url: '/employee/alfa_auth/',
      data: {
        code,
        state,
        redirect_url,
        client_id
      },
      headers: {
        ['Authorization']: null,
      }
    })
    const {access, refresh} = tokenRes.data

    if (access && refresh) {
      yield call(fetchUser, {access, refresh})
    }

  } catch (e) {
    if (e?.response?.data?.message === 'Code has expired') {
      yield put(userSlice.actions.logout())
      yield put(appSlice.actions.addMessage({
        type: 'error',
        text: e?.response?.data?.detail ?? 'Что-то пошло не так',
      }))
      yield put(userSlice.actions.setLoading(false))
      yield put(userSlice.actions.setTokenLoading(false))
      return
    }
    if (e?.response?.data?.message === 'User disabled') {
      yield put(appSlice.actions.addMessage({
        type: 'error',
        text: e?.response?.data?.detail ?? 'Что-то пошло не так',
      }))
      yield put(userSlice.actions.setUserDisabled(true))
      yield put(userSlice.actions.setLoading(false))
      yield put(userSlice.actions.setTokenLoading(false))
      return
    }
    if (e?.code === 'ERR_NETWORK') {
      yield put(appSlice.actions.addMessage({
        type: 'error',
        text: 'Ошибка доступа к серверу',
      }))
    } else {
      if (e?.response?.status === 401) {
        yield put(appSlice.actions.addMessage({
          type: 'error',
          text: e?.response?.data?.detail ?? 'Что-то пошло не так',
        }))
      } else {
        yield put(appSlice.actions.addMessage({
          type: 'error',
          text: 'Что-то пошло не так',
        }))
      }
    }
  }
  yield put(userSlice.actions.setTokenLoading(false))
  yield put(userSlice.actions.setLoading(false))
  yield put(userSlice.actions.setAlfa(null))
}

function* logout(action) {
  const silent = action?.payload?.silent === true

  Sentry.setUser(null);

  let refreshToken = null
  let accessToken = null
  try {
    const userState = yield select(store => store.user)
    refreshToken = userState?.tokens?.refresh
    accessToken = userState?.tokens?.access

    if (!refreshToken || !accessToken) {
      const storedTokens = JSON.parse(localStorage.getItem('tokens') || 'null')
      refreshToken = refreshToken ?? storedTokens?.refresh ?? null
      accessToken = accessToken ?? storedTokens?.access ?? null
    }
  } catch (e) {
    console.error(e)
  }

  localStorage.removeItem('tokens')
  localStorage.removeItem('permissions')
  localStorage.removeItem('allPermissions')
  localStorage.removeItem('sellerDocs')
  // Иначе следующий вход под тем же мерчантом не перечитает справочники.
  yield put(appSlice.actions.setDirectoriesMerchantId(null))
  network.axiosUpdate()
  yield put(notificationsSlice.actions.stopPollMessages())
  yield put(notificationsSlice.actions.setGlobalNotificationsFilters(null))
  yield put(userSlice.actions.setUser({
    user: null,
    tokens: null,
    permissions: null,
    balances: null,
    limits: null,
    otp: null,
  }))

  if (!silent && refreshToken) {
    const headers = {
      'Accept-Language': 'ru',
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    }
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    axios({
      baseURL: process.env.REACT_APP_API_URI ? process.env.REACT_APP_API_URI : "/",
      method: 'POST',
      url: '/token/blacklist/',
      data: {refresh: refreshToken},
      headers,
    }).catch(e => console.error(e))
  }
}

export function* userSagas() {
  yield takeLatest(userSlice.actions.restore.type, restore)
  yield takeLatest(userSlice.actions.logout.type, logout)
  yield takeLatest(userSlice.actions.login.type, login)
  yield takeLatest(userSlice.actions.otpCheck.type, otpCheck)
  yield takeLatest(userSlice.actions.userLimit.type, userLimit)
  yield takeLatest(userSlice.actions.userBalances.type, userBalances)
  yield takeLatest(userSlice.actions.refreshUserBalances.type, refreshUserBalances)
  yield takeLatest(userSlice.actions.alfaLogin.type, alfaLogin)
  yield takeLatest([userSlice.actions.fetchGetMy.type, userSlice.actions.logout.type], fetchGetMy)
  yield takeLatest([userSlice.actions.refreshPermissions.type, userSlice.actions.logout.type], refreshPermissions)
  yield takeLatest(endpointSlice.actions.selectEndpoint.type, fetchDirectories)
  // Балансы приходят по точкам: при переключении их надо перечитать, иначе для
  // точки, созданной уже в этой сессии, баланса в сторе нет вовсе.
  yield takeLatest(endpointSlice.actions.selectEndpoint.type, userBalances)
}
